import { router } from "@inertiajs/react"
import { useCallback } from "react"

import { flushListCache, flushListsIndexCache } from "@/lib/cache"
import {
  listTaskArchivalPath,
  listTaskCompletionPath,
  listTaskExtensionPath,
  listTaskPath,
} from "@/routes"
import type { Task } from "@/types"

interface UseTaskActionsOptions<
  T extends Pick<Task, "id" | "status" | "description">,
> {
  listId: number
  task: T
  onDelete?: (task: T) => void
}

interface UseTaskActionsReturn {
  handleComplete: (e?: React.MouseEvent) => void
  handleArchive: () => void
  handleUnarchive: () => void
  handleExtend: () => void
  handleDelete: () => void
}

export function useTaskActions<
  T extends Pick<Task, "id" | "status" | "description">,
>({ listId, task, onDelete }: UseTaskActionsOptions<T>): UseTaskActionsReturn {
  const flushCache = useCallback(() => {
    // Always flush both caches since task state changes affect:
    // 1. The specific list view (all status tabs)
    // 2. The aggregate "All Tasks" view (/lists)
    // This ensures prefetched data is invalidated everywhere
    flushListCache(listId)
    flushListsIndexCache()
  }, [listId])

  const handleComplete = useCallback(
    (e?: React.MouseEvent) => {
      // Alt/Option + click to delete instead of complete
      if (e?.altKey) {
        if (onDelete) {
          onDelete(task)
        }
        return
      }

      if (task.status === "completed") {
        router.delete(listTaskCompletionPath(listId, task.id), {
          preserveScroll: true,
          onSuccess: flushCache,
        })
      } else {
        router.post(
          listTaskCompletionPath(listId, task.id),
          {},
          {
            preserveScroll: true,
            onSuccess: flushCache,
          },
        )
      }
    },
    [task, listId, flushCache, onDelete],
  )

  const handleArchive = useCallback(() => {
    router.post(
      listTaskArchivalPath(listId, task.id),
      {},
      {
        preserveScroll: true,
        onSuccess: flushCache,
      },
    )
  }, [listId, task.id, flushCache])

  const handleUnarchive = useCallback(() => {
    router.delete(listTaskArchivalPath(listId, task.id), {
      preserveScroll: true,
      onSuccess: flushCache,
    })
  }, [listId, task.id, flushCache])

  const handleExtend = useCallback(() => {
    router.post(
      listTaskExtensionPath(listId, task.id),
      {},
      {
        preserveScroll: true,
        onSuccess: flushCache,
      },
    )
  }, [listId, task.id, flushCache])

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete(task)
    } else {
      // Fallback to browser confirm if no delete handler provided
      if (confirm("Are you sure you want to delete this task?")) {
        router.delete(listTaskPath(listId, task.id), {
          preserveScroll: true,
          onSuccess: flushCache,
        })
      }
    }
  }, [task, onDelete, listId, flushCache])

  return {
    handleComplete,
    handleArchive,
    handleUnarchive,
    handleExtend,
    handleDelete,
  }
}
